import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { templatesApi } from '../../../api/templates.api';

const TemplateStatusPage = ({ sess = { uid: '', role: 'user' } }) => {
  const theme = useTheme();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await templatesApi.getStatus();
      const list = response?.data?.data || response?.data || [];
      setStatuses(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching template statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (status) => {
    const map = {
      approved: { label: 'Approved', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      rejected: { label: 'Rejected', color: 'error' },
      draft: { label: 'Draft', color: 'default' },
    };
    const conf = map[status] || map.pending;
    return <Chip label={conf.label} color={conf.color} size="small" variant="outlined" />;
  };

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/templates" sx={secondaryButtonSx}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={700}>Template Status</Typography>
                <Typography variant="body2" color="text.secondary">
                  Track approval progress at a glance
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={fetchStatuses}
              disabled={loading}
              sx={secondaryButtonSx}
            >
              Refresh
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          <Divider sx={{ mb: 2, opacity: 0.6 }} />
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : statuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" color="text.secondary">
                        No template statuses found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  statuses.map((tpl, index) => (
                    <TableRow
                      key={tpl.t_id || index}
                      hover
                      sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{tpl.t_tname || '-'}</TableCell>
                      <TableCell>{tpl.t_language || '-'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{tpl.t_type || '-'}</TableCell>
                      <TableCell>{statusChip(tpl.t_status)}</TableCell>
                      <TableCell>
                        {tpl.updated_at ? new Date(tpl.updated_at).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemplateStatusPage;
